import React, { useState, useEffect } from "react";
import {
    Table, TableBody, TableHead, Toolbar, Typography, CircularProgress,
    Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel, Chip
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import {
    getSubscriptionPlans,
    updateSubscriptionPlan,
    createSubscriptionPlan
} from "../services/subscriptionPlanService";
import {
    StyledTableContainer, StyledTableCell, StyledTableRow, StyledPaper
} from "../assets/styles/tableStyle";
import { SubscriptionPlan } from "../types/SubscriptionPlan";

const SubscriptionPlans: React.FC = () => {
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // Edit/Create State
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState<Partial<SubscriptionPlan>>({});

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await getSubscriptionPlans();
            // Handle response data structure
            setPlans(Array.isArray(res.data) ? res.data : (res.data as any).data || []);
        } catch (err) {
            console.error("Failed to load plans", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    const handleAddNew = () => {
        setIsEditing(false);
        setForm({
            name: '',
            description: '',
            price: 0,
            interval: 'daily', // Default value
            maxRides: null,
            isActive: true
        });
        setOpen(true);
    };

    const handleEdit = (plan: SubscriptionPlan) => {
        setIsEditing(true);
        setForm(plan);
        setOpen(true);
    };

    const handleSave = async () => {
        try {
            if (isEditing && form.id) {
                await updateSubscriptionPlan(form.id, form);
            } else {
                await createSubscriptionPlan(form);
            }
            setOpen(false);
            fetchPlans();
        } catch (err) {
            console.error(err);
            alert("Failed to save plan");
        }
    };

    const handleChange = (field: keyof SubscriptionPlan, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Typography variant="h6">Subscription Plans (Revenue Models)</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddNew}
                >
                    Add Plan
                </Button>
            </Toolbar>

            {loading ? <CircularProgress sx={{ m: 2 }} /> : (
                <StyledTableContainer>
                    <Table>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>Name</StyledTableCell>
                                <StyledTableCell>Price (₹)</StyledTableCell>
                                <StyledTableCell>Interval</StyledTableCell>
                                <StyledTableCell>Ride Cap</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Action</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {plans.map((plan) => (
                                <StyledTableRow key={plan.id}>
                                    <StyledTableCell>{plan.id}</StyledTableCell>
                                    <StyledTableCell>
                                        <b>{plan.name}</b><br/>
                                        <span style={{fontSize: '0.8em', color: '#666'}}>{plan.description}</span>
                                    </StyledTableCell>
                                    <StyledTableCell>₹{plan.price}</StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={plan.interval} size="small" variant="outlined" />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {plan.maxRides ? `${plan.maxRides} Rides / Day` : "Unlimited"}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <span style={{
                                            color: plan.isActive ? 'green' : 'red',
                                            fontWeight: 'bold'
                                        }}>
                                            {plan.isActive ? "Active" : "Inactive"}
                                        </span>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Button startIcon={<EditIcon/>} size="small" onClick={() => handleEdit(plan)}>
                                            Edit
                                        </Button>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            )}

            {/* Edit/Create Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{isEditing ? "Edit Plan" : "Create New Plan"}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Name"
                        value={form.name || ''}
                        onChange={e => handleChange('name', e.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Description"
                        value={form.description || ''}
                        onChange={e => handleChange('description', e.target.value)}
                        fullWidth
                    />
                    <div style={{ display: 'flex', gap: 10 }}>
                        <TextField
                            label="Price"
                            type="number"
                            value={form.price || ''}
                            onChange={e => handleChange('price', parseFloat(e.target.value))}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel>Interval</InputLabel>
                            <Select
                                value={form.interval || 'daily'}
                                label="Interval"
                                onChange={e => handleChange('interval', e.target.value)}
                            >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    <TextField
                        label="Max Rides (Leave empty for Unlimited)"
                        type="number"
                        value={form.maxRides === null ? '' : form.maxRides}
                        onChange={e => handleChange('maxRides', e.target.value === '' ? null : parseInt(e.target.value))}
                        helperText="If set, drivers can only take this many rides per interval."
                        fullWidth
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.isActive || false}
                                onChange={e => handleChange('isActive', e.target.checked)}
                            />
                        }
                        label="Plan is Active (Drivers can buy)"
                    />

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>
                        {isEditing ? "Save Changes" : "Create Plan"}
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledPaper>
    );
};

export default SubscriptionPlans;